import type { Event } from 'nostr-tools/core';
import { SimplePool } from 'nostr-tools/pool';
import type { BadgeAward } from './api';
import type { SignerConnection } from './signers';
import { requireExactSignerResult } from './signerSafety';

export type ProfilePublishResult = {
  event: Event | null;
  alreadyAccepted: boolean;
  acceptedRelays: string[];
  failedRelays: string[];
};

export async function acceptBadge(
  connection: SignerConnection,
  award: BadgeAward,
  relays: string[]
): Promise<ProfilePublishResult> {
  if (award.revocation) throw new Error('This badge award has been revoked by its issuer.');
  if (!award.recipients.includes(connection.pubkey)) throw new Error('This badge was not awarded to the connected key.');
  const pool = new SimplePool();
  try {
    const existing = await pool.querySync(relays, { kinds: [10008], authors: [connection.pubkey], limit: 20 }, { maxWait: 6000 });
    const latest = existing.sort((a, b) => b.created_at - a.created_at)[0];
    const alreadyAccepted = hasPair(latest?.tags ?? [], award.badge_address, award.event_id);
    if (alreadyAccepted) return { event: latest, alreadyAccepted: true, acceptedRelays: [], failedRelays: [] };
    const tags = [...(latest?.tags ?? []), ['a', award.badge_address], ['e', award.event_id, relays[0] ?? '']];
		const unsigned = { kind: 10008, tags, content: '', created_at: Math.floor(Date.now() / 1000) };
		const event = requireExactSignerResult(unsigned, await connection.signer.signEvent(unsigned), connection.pubkey);
    const { acceptedRelays, failedRelays } = await publishWithPool(pool, event, relays);
    return { event, alreadyAccepted: false, acceptedRelays, failedRelays };
  } finally {
    pool.destroy();
  }
}

export async function republishProfileBadge(event: Event, relays: string[]): Promise<{ acceptedRelays: string[]; failedRelays: string[] }> {
  const pool = new SimplePool();
  try {
    return await publishWithPool(pool, event, relays);
  } finally {
    pool.destroy();
  }
}

async function publishWithPool(pool: SimplePool, event: Event, relays: string[]) {
  const settled = await Promise.allSettled(pool.publish(relays, event, { maxWait: 9000 }));
  const acceptedRelays: string[] = [];
  const failedRelays: string[] = [];
  settled.forEach((result, index) => (result.status === 'fulfilled' ? acceptedRelays : failedRelays).push(relays[index]));
  return { acceptedRelays, failedRelays };
}

export function hasPair(tags: string[][], address: string, eventID: string): boolean {
  for (let index = 0; index < tags.length - 1; index++) {
    if (tags[index][0] === 'a' && tags[index][1] === address && tags[index + 1][0] === 'e' && tags[index + 1][1] === eventID) return true;
  }
  return false;
}
